/**
 * C: "Review your plan" / "One last look." — the in-place checkout review step
 * shared by the onboarding chooser (A) and the paywall dialog (B) on web.
 *
 * States come from the shared checkout machine (`useCheckoutFlow`):
 * review | purchasing | restoring | redirecting | pending | checking |
 * cancelled | error | unchanged. `pending`/`checking` show "We're checking
 * your subscription." with Check again (sync only, never a second purchase).
 * The host owns `purchased` (onboarding advances; the paywall shows Welcome).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckoutState } from '../../lib/subscription-journey/checkout-flow';
import {
  CHECKOUT_STATE_COPY,
  checkoutFailureCopy,
  purchaseProviderKey,
} from '../../lib/subscription-journey/checkout-flow';
import type { TierPrices } from '../../lib/subscription-journey/pricing';
import { getCyclePrice } from '../../lib/subscription-journey/pricing';
import { NovaMood } from './NovaMood.web';
import {
  Icon,
  InfoDialog,
  StateBlock,
  SubButton,
  SubGroup,
  SubHeading,
  SubInfo,
  SubRow,
  LegalLinks,
} from './journey-ui.web';

const TIER_NAMES = { plus: 'Plus', pro: 'Pro' } as const;

type InfoKey = 'terms' | 'provider' | null;

export function isCheckoutPendingStatus(status: CheckoutState['status']) {
  return status === 'pending' || status === 'checking';
}

export function CheckoutReviewBody({
  state,
  prices,
  onChangePlan,
}: {
  state: CheckoutState;
  prices: TierPrices | null;
  /** "Tabbio {tier}" row: back to the picker. */
  onChangePlan: () => void;
}) {
  const { t } = useTranslation('common');
  const [info, setInfo] = useState<InfoKey>(null);
  const selection = state.selection;
  if (!selection) return null;
  const provider = t(purchaseProviderKey('web'));

  if (isCheckoutPendingStatus(state.status)) {
    return (
      <div style={{ paddingTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <NovaMood mood="calm" size={104} />
        </div>
        <StateBlock
          spinner={state.status === 'checking'}
          title={t('subscription.checkout.checking')}
          body={t('subscription.checkout.delayed')}
        />
      </div>
    );
  }

  const cycle = getCyclePrice(prices, selection.cycle);
  // An error names its reason (store / network / unavailable / sign-in);
  // cancelled and unchanged keep their one-line notes.
  const notice =
    state.status === 'error'
      ? checkoutFailureCopy(state.failure)
      : state.status === 'cancelled' || state.status === 'unchanged'
        ? CHECKOUT_STATE_COPY[state.status]
        : null;
  const tierName = TIER_NAMES[selection.tier];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 14px' }}>
        <NovaMood mood="calm" size={112} />
      </div>
      <SubHeading
        title={t('subscription.checkout.heading')}
        subtitle={t('subscription.checkout.subtitle')}
      />
      {notice ? (
        <div role="alert">
          <SubInfo icon="warning" warning>
            {t(notice.titleKey, { provider })}
            {notice.bodyKey ? ` ${t(notice.bodyKey, { provider })}` : ''}
          </SubInfo>
        </div>
      ) : null}
      <div style={{ marginTop: 22 }}>
        <SubGroup>
          <SubRow
            icon="verified_user"
            title={t('subscription.checkout.planRow', { tier: tierName })}
            sub={t(
              selection.cycle === 'yearly'
                ? 'subscription.checkout.yearlySubscription'
                : 'subscription.checkout.monthlySubscription',
            )}
            onClick={onChangePlan}
          />
          <SubRow
            icon="credit_card"
            title={t('subscription.checkout.totalToday')}
            sub={cycle?.chargeDisplay ?? '—'}
          />
          <SubRow
            icon="autorenew"
            title={t('subscription.checkout.renewsAutomatically')}
            sub={t(
              selection.cycle === 'yearly'
                ? 'subscription.checkout.everyYear'
                : 'subscription.checkout.everyMonth',
            )}
            onClick={() => setInfo('terms')}
          />
          <SubRow
            icon="lock"
            title={t('subscription.checkout.billedThrough')}
            sub={provider}
            onClick={() => setInfo('provider')}
          />
        </SubGroup>
      </div>
      <SubInfo>{t('subscription.checkout.accessNote')}</SubInfo>
      <p className="tabbio-sub-fine">{t('subscription.checkout.fine')}</p>
      {info === 'terms' ? (
        <InfoDialog
          title={t('subscription.info.terms.title')}
          body={t('subscription.info.terms.body')}
          onClose={() => setInfo(null)}
          zIndex={10050}
        />
      ) : null}
      {info === 'provider' ? (
        <InfoDialog
          title={provider}
          body={t('subscription.info.provider.body')}
          onClose={() => setInfo(null)}
          zIndex={10050}
        />
      ) : null}
    </div>
  );
}

export function CheckoutReviewActions({
  state,
  onPurchase,
  onRestore,
  onCheckAgain,
  onBackToTabbio,
}: {
  state: CheckoutState;
  onPurchase: () => void;
  onRestore: () => void;
  onCheckAgain: () => void;
  onBackToTabbio: () => void;
}) {
  const { t } = useTranslation('common');
  const provider = t(purchaseProviderKey('web'));

  if (isCheckoutPendingStatus(state.status)) {
    const checking = state.status === 'checking';
    return (
      <>
        <SubButton
          onClick={onCheckAgain}
          disabled={checking}
          aria-busy={checking}
          icon={<Icon name="refresh" />}
        >
          {t('subscription.checkout.checkAgain')}
        </SubButton>
        <SubButton kind="text" onClick={onBackToTabbio} disabled={checking}>
          {t('subscription.checkout.backToTabbio')}
        </SubButton>
      </>
    );
  }

  const busy =
    state.status === 'purchasing' ||
    state.status === 'restoring' ||
    state.status === 'redirecting';
  return (
    <>
      <SubButton
        onClick={onPurchase}
        disabled={busy}
        aria-busy={state.status === 'purchasing' || state.status === 'redirecting'}
        icon={
          state.status === 'purchasing' || state.status === 'redirecting' ? (
            <span className="tabbio-sub-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} aria-hidden />
          ) : (
            <Icon name="lock" />
          )
        }
      >
        {t('subscription.checkout.continueTo', { provider })}
      </SubButton>
      <SubButton kind="text" onClick={onRestore} disabled={busy} aria-busy={state.status === 'restoring'}>
        {t('subscription.checkout.restoreExisting')}
      </SubButton>
      {/* Apple's sign-up screen requirements: Terms and Privacy stay reachable
          on the step before the provider purchase. */}
      <LegalLinks />
    </>
  );
}
